/**
 * Sticky header with show/hide on scroll
 * Automatically hides header when scrolling down and shows when scrolling up or near top
 *
 * Two independent mechanisms, deliberately not conflated:
 *  - `data-scrolled` flips the header from the hero's colours to solid, and is tied to
 *    the transparent-header treatment (see `utilities.scss`).
 *  - Hold zones keep the header visible while scrolling through a section that has
 *    "Maintain Sticky Header" enabled, regardless of how that section is styled.
 */

import { loadDeferredImages } from '@/components/helpers/deferred-images'

interface HoldZone {
  top: number
  bottom: number
}

export class StickyHeader extends HTMLElement {
  static TRANSITION = 'top 0.3s ease'
  static UI_THRESHOLD = 200
  static SCROLL_DELAY = 300
  static FIRST_SECTION_SELECTOR = '#MainContent > .shopify-section'
  static TRANSPARENT_HEADER_SELECTOR = '.transparent'
  static HOLD_ZONE_SELECTOR = '#MainContent .maintain-sticky-header'
  static UI_VISIBLE_SELECTOR = '[data-ui~="visible"]'
  static MENU_ITEM_SELECTOR = '[data-header-menu-item]'
  lastScrollY: number
  ticking: boolean
  resizeTicking = false
  isVisible: boolean
  isScrolled: boolean
  topOffset = 0
  elementHeight = 0
  scrollThreshold = 0
  uiThreshold: number = StickyHeader.UI_THRESHOLD
  holdZones: HoldZone[] = []
  headerElement: HTMLElement | null = null
  scrolledTimeout: ReturnType<typeof setTimeout> | null = null
  ignoreDirectionUntil = 0
  lastDocHeight = 0
  boundMenuItemEnter: ((e: MouseEvent) => void) | null = null
  boundResize: (() => void) | null = null
  boundSectionLoad: (() => void) | null = null
  boundBlockSelect: ((e: Event) => void) | null = null
  boundBlockDeselect: ((e: Event) => void) | null = null

  constructor() {
    super()

    // State variables
    this.lastScrollY = window.scrollY
    this.ticking = false
    this.isVisible = true
    this.isScrolled = false

    this.style.transition = StickyHeader.TRANSITION

    // Bind scroll handler
    this.handleScroll = this.handleScroll.bind(this)
    this.handleMouseLeave = this.handleMouseLeave.bind(this)
    this.boundMenuItemEnter = this.handleMenuItemEnter.bind(this)
    this.boundResize = this.handleResize.bind(this)
    this.boundSectionLoad = this.measure.bind(this)
    this.boundBlockSelect = this.handleBlockSelect.bind(this)
    this.boundBlockDeselect = this.handleBlockDeselect.bind(this)
  }

  get hiddenPosition() {
    return `-${this.elementHeight + this.topOffset}px`
  }

  get visiblePosition() {
    return `${this.topOffset}px`
  }

  connectedCallback() {
    this.measure()
    this.lastDocHeight = document.documentElement.scrollHeight

    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.boundResize!, { passive: true })
    this.addEventListener('mouseleave', this.handleMouseLeave)
    this.addEventListener('mouseover', this.boundMenuItemEnter!)

    // In the theme editor, reveal the dropdown when a merchant selects a block
    // inside it (banner, cards or submenu) so they can see what they're editing.
    if ((window as unknown as { Shopify?: { designMode?: boolean } }).Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.boundBlockSelect!)
      this.addEventListener('shopify:block:deselect', this.boundBlockDeselect!)

      // Toggling "Maintain Sticky Header" re-renders that section but not this one,
      // so re-measure rather than leaving the merchant to reload.
      document.addEventListener('shopify:section:load', this.boundSectionLoad!)
    }

    // Browsers restore scroll position on reload. Without this, a page reloaded
    // mid-scroll keeps the header in its at-the-top state — transparent over content —
    // until the first scroll event.
    this.handleScroll()
  }

  disconnectedCallback() {
    if (this.scrolledTimeout) {
      clearTimeout(this.scrolledTimeout)
      this.scrolledTimeout = null
    }
    window.removeEventListener('scroll', this.handleScroll)
    this.removeEventListener('mouseleave', this.handleMouseLeave)
    if (this.boundResize) {
      window.removeEventListener('resize', this.boundResize)
    }
    if (this.boundMenuItemEnter) {
      this.removeEventListener('mouseover', this.boundMenuItemEnter)
    }
    if (this.boundSectionLoad) {
      document.removeEventListener('shopify:section:load', this.boundSectionLoad)
    }
    if (this.boundBlockSelect) {
      this.removeEventListener('shopify:block:select', this.boundBlockSelect)
    }
    if (this.boundBlockDeselect) {
      this.removeEventListener('shopify:block:deselect', this.boundBlockDeselect)
    }
  }

  measure() {
    this.measureTopOffset()
    this.headerElement = this.querySelector('#header')
    this.elementHeight = this.headerElement ? this.headerElement.getBoundingClientRect().height : this.getBoundingClientRect().height
    this.updateThresholds()
    this.measureHoldZones()
  }

  /**
   * `top` is both the resting offset and the animated property, so once show/hide
   * has written an inline value the computed style reports our own position, not
   * the offset. Clear the inline value before reading, with the transition
   * suppressed so the round trip cannot animate. A resize landing mid-slide snaps
   * to the slide's destination — accepted.
   */
  measureTopOffset() {
    const inlineTop = this.style.top

    if (!inlineTop) {
      this.topOffset = parseInt(window.getComputedStyle(this).top.replace('px', '')) || 0
      return
    }

    this.style.transition = 'none'
    this.style.top = ''
    this.topOffset = parseInt(window.getComputedStyle(this).top.replace('px', '')) || 0
    this.style.top = inlineTop
    this.flushStyles()
    this.style.transition = StickyHeader.TRANSITION
  }

  /**
   * Reads a layout property to force a synchronous style flush, committing whatever was
   * just written while transitions are still disabled.
   */
  flushStyles() {
    return this.offsetHeight
  }

  updateThresholds() {
    const firstSection = document.querySelector<HTMLElement>(StickyHeader.FIRST_SECTION_SELECTOR)
    const isTransparentHeaderSection = firstSection?.querySelector(StickyHeader.TRANSPARENT_HEADER_SELECTOR) != null

    // A transparent section also carries `-mt-header`, so it starts at document offset 0
    // and its bottom sits at exactly its own height. Do not extend this branch to
    // non-transparent sections without adding the header height back on.
    this.scrollThreshold = firstSection && isTransparentHeaderSection
      ? firstSection.getBoundingClientRect().height
      : window.innerHeight / 2
  }

  /**
   * Ranges are document offsets rather than bare heights, so a hold zone is measured
   * correctly whether or not its section is pulled up under the header.
   */
  measureHoldZones() {
    this.holdZones = [...document.querySelectorAll<HTMLElement>(StickyHeader.HOLD_ZONE_SELECTOR)].map((el) => {
      const rect = el.getBoundingClientRect()
      const top = rect.top + window.scrollY

      return { top, bottom: top + rect.height }
    })
  }

  isInHoldZone(scrollY: number) {
    return this.holdZones.some((zone) => scrollY >= zone.top && scrollY < zone.bottom)
  }

  handleResize() {
    if (this.resizeTicking) return
    this.resizeTicking = true

    window.requestAnimationFrame(() => {
      this.measure()

      // Header height changes across the 1024px breakpoint. Only re-apply the inline
      // `top` when show/hide already owns positioning — an untouched header rests on
      // its class-driven `top`, and an inline value would shadow the stylesheet.
      const top = this.isVisible ? this.visiblePosition : this.hiddenPosition
      if (this.style.top && this.style.top !== top) {
        this.style.top = top
      }

      // The published height feeds --sticky-offset, so a breakpoint crossing must
      // refresh it.
      this.updateHeaderHeightVariable(this.isVisible)

      this.resizeTicking = false
    })
  }

  handleBlockSelect(e: Event) {
    const item = (e.target as HTMLElement).closest<HTMLElement>(StickyHeader.MENU_ITEM_SELECTOR)
    if (item) {
      this.showHeader()
      this.setOpenSubmenu(item)
    }
  }

  handleBlockDeselect() {
    this.clearOpenSubmenu()
  }

  handleMouseLeave() {
    this.clearOpenSubmenu()
  }

  handleMenuItemEnter(e: MouseEvent) {
    const item = (e.target as HTMLElement).closest<HTMLElement>(StickyHeader.MENU_ITEM_SELECTOR)
    if (item) {
      this.setOpenSubmenu(item)
    }
  }

  setOpenSubmenu(menuItem: HTMLElement) {
    if (menuItem.getAttribute('data-submenu-open') === 'true') return
    this.querySelectorAll(StickyHeader.MENU_ITEM_SELECTOR).forEach((el) => {
      el.removeAttribute('data-submenu-open')
    })

    // The dropdown is `invisible`, not hidden, so its cards would otherwise be
    // downloaded on page load despite never being seen. Attach them here instead.
    loadDeferredImages(menuItem)

    menuItem.setAttribute('data-submenu-open', 'true')
  }

  clearOpenSubmenu() {
    this.querySelectorAll(StickyHeader.MENU_ITEM_SELECTOR).forEach((el) => {
      el.removeAttribute('data-submenu-open')
    })
  }

  checkForVisibleUI() {
    return this.querySelector(StickyHeader.UI_VISIBLE_SELECTOR)
  }

  updateHeaderHeightVariable(isVisible: boolean) {
    document.documentElement.style.setProperty(
      '--height-header-adapt',
      isVisible ? `${this.elementHeight}px` : '0px'
    )
  }

  showHeader() {
    if (!this.isVisible) {
      this.style.top = this.visiblePosition
      this.isVisible = true
      this.setAttribute('data-visible', 'visible')
      this.updateHeaderHeightVariable(true)
      this.suppressDirectionDetection()
    }
  }

  hideHeader() {
    if (this.isVisible && !this.checkForVisibleUI()) {
      this.style.top = this.hiddenPosition
      this.isVisible = false
      this.removeAttribute('data-visible')
      this.updateHeaderHeightVariable(false)
      this.suppressDirectionDetection()
    }
  }

  /**
   * Toggling visibility can change document height (e.g. a sub-bar collapsing),
   * which nudges scrollY and would otherwise be misread as a user scroll,
   * flipping the header back and causing flicker. Ignore direction detection
   * for the duration of the visibility transition.
   */
  suppressDirectionDetection() {
    this.ignoreDirectionUntil = performance.now() + StickyHeader.SCROLL_DELAY
  }

  registerScroll() {
    if (this.isScrolled) return
    this.isScrolled = true
    this.scheduleScrolledState(true)
  }

  unregisterScroll() {
    if (!this.isScrolled) return
    this.isScrolled = false
    this.scheduleScrolledState(false)
  }

  scheduleScrolledState(scrolled: boolean) {
    if (this.scrolledTimeout) clearTimeout(this.scrolledTimeout)

    // Solid is the always-legible state: apply it at once, and only debounce the return
    // to transparent so rubber-banding at the hero edge can't strobe. Debouncing both
    // directions would leave the header in hero colours over the section below it.
    if (scrolled) {
      this.setAttribute('data-scrolled', 'true')
      return
    }

    this.scrolledTimeout = setTimeout(() => {
      this.removeAttribute('data-scrolled')
      this.scrolledTimeout = null
    }, StickyHeader.SCROLL_DELAY)
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        const currentScroll = window.scrollY
        const currentDocHeight = document.documentElement.scrollHeight
        const docHeightChanged = currentDocHeight !== this.lastDocHeight
        this.lastDocHeight = currentDocHeight

        if (currentScroll < this.scrollThreshold) {
          this.unregisterScroll()
        } else {
          this.registerScroll()
        }

        if (docHeightChanged) {
          // Content loading in above a hold zone shifts its document offsets.
          this.measureHoldZones()
          this.suppressDirectionDetection()
        }

        if (docHeightChanged || performance.now() < this.ignoreDirectionUntil) {
          this.lastScrollY = currentScroll
          this.ticking = false
          return
        }

        // Show header inside a hold zone, at the top, or when scrolling up
        if (this.isInHoldZone(currentScroll) || currentScroll < this.uiThreshold || (currentScroll < this.lastScrollY && currentScroll > this.uiThreshold)) {
          this.showHeader()
        } else if (currentScroll > this.lastScrollY && currentScroll > this.uiThreshold) {
          // Hide header if scrolling down past threshold
          this.hideHeader()
        }
        this.lastScrollY = currentScroll
        this.ticking = false
      })

      this.ticking = true
    }
  }
}
